import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';

// Minutes between GPS arrival and clock-in (or clock-out and departure)
// worth flagging as time wasted on site.
const FLAG_MINUTES = 10;

const torontoToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });

// TKT-422: the truck breakdown sheet's "Min Truck Arrive / Clock in" and
// "Min Truck leave / Clock Out" columns, from GPS arrival/departure at each
// property, plus time on site against the allotted time.
const SiteTimeReport = () => {
  const { t, i18n } = useTranslation();
  const [date, setDate] = useState(torontoToday());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await makeApiCall(`api/reports/site-time?date=${encodeURIComponent(date)}`);
      if (!response.ok) throw new Error();
      setReport(await response.json());
    } catch (err) {
      setError(t('reports.siteTime.loadError'));
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [date, t]);

  useEffect(() => {
    load();
  }, [load]);

  const formatTime = (iso) => (iso
    ? new Date(iso).toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' })
    : '—');

  const renderGap = (minutes, kind) => {
    if (minutes == null) return '—';
    if (minutes < 0) {
      return (
        <Tooltip title={t(kind === 'arrive' ? 'reports.siteTime.clockedBeforeArrival' : 'reports.siteTime.clockedAfterLeaving')}>
          <Chip size="small" variant="outlined" color="info" label={t('reports.siteTime.minutes', { count: minutes })} />
        </Tooltip>
      );
    }
    return minutes >= FLAG_MINUTES
      ? <Chip size="small" color="warning" label={t('reports.siteTime.minutes', { count: minutes })} />
      : t('reports.siteTime.minutes', { count: minutes });
  };

  const renderOverUnder = (minutes) => {
    if (minutes == null) return '—';
    const label = minutes > 0 ? `+${minutes}` : String(minutes);
    return (
      <Typography variant="body2" component="span" color={minutes > 0 ? 'error.main' : 'success.main'}>
        {label}
      </Typography>
    );
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        {t('reports.siteTime.intro')}
      </Alert>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          type="date"
          size="small"
          label={t('reports.siteTime.date')}
          value={date}
          onChange={(event) => setDate(event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          {t('reports.siteTime.refresh')}
        </Button>
        {loading && <CircularProgress size={24} />}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {report && report.trucks.length === 0 && !loading && (
        <Typography color="text.secondary">{t('reports.siteTime.noStops')}</Typography>
      )}

      {report?.trucks.map((truck) => (
        <Paper key={truck.truck_id} variant="outlined" sx={{ mb: 3, p: { xs: 1, sm: 2 } }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ mr: 1 }}>
              {truck.truck_name}
            </Typography>
            <Chip
              size="small"
              label={t('reports.siteTime.totalArriveToClockIn', { count: truck.totals.arrive_to_clock_in_minutes })}
            />
            <Chip
              size="small"
              label={t('reports.siteTime.totalClockOutToLeave', { count: truck.totals.clock_out_to_leave_minutes })}
            />
            <Chip
              size="small"
              color={truck.totals.over_under_minutes > 0 ? 'error' : 'success'}
              variant="outlined"
              label={t('reports.siteTime.totalOverUnder', {
                value: truck.totals.over_under_minutes > 0
                  ? `+${truck.totals.over_under_minutes}`
                  : truck.totals.over_under_minutes,
              })}
            />
          </Stack>
          <TableContainer>
            <Table size="small" aria-label={t('reports.siteTime.tableLabel', { truck: truck.truck_name })}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('reports.siteTime.customer')}</TableCell>
                  <TableCell align="right">{t('reports.siteTime.allotted')}</TableCell>
                  <TableCell>{t('reports.siteTime.truckArrived')}</TableCell>
                  <TableCell>{t('reports.siteTime.clockIn')}</TableCell>
                  <TableCell align="right">{t('reports.siteTime.arriveToClockIn')}</TableCell>
                  <TableCell>{t('reports.siteTime.clockOut')}</TableCell>
                  <TableCell>{t('reports.siteTime.truckLeft')}</TableCell>
                  <TableCell align="right">{t('reports.siteTime.clockOutToLeave')}</TableCell>
                  <TableCell align="right">{t('reports.siteTime.onSite')}</TableCell>
                  <TableCell align="right">{t('reports.siteTime.overUnder')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {truck.stops.map((stop) => (
                  <TableRow key={stop.active_job_id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{stop.customer_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{stop.address}</Typography>
                      {!stop.has_location && (
                        <Typography variant="caption" color="warning.main" display="block">
                          {t('reports.siteTime.noLocation')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {stop.allotted_minutes > 0 ? t('reports.siteTime.minutes', { count: stop.allotted_minutes }) : '—'}
                    </TableCell>
                    <TableCell>{formatTime(stop.gps_arrived_at)}</TableCell>
                    <TableCell>{formatTime(stop.clock_in_time)}</TableCell>
                    <TableCell align="right">{renderGap(stop.arrive_to_clock_in_minutes, 'arrive')}</TableCell>
                    <TableCell>{formatTime(stop.clock_out_time)}</TableCell>
                    <TableCell>{formatTime(stop.gps_left_at)}</TableCell>
                    <TableCell align="right">{renderGap(stop.clock_out_to_leave_minutes, 'leave')}</TableCell>
                    <TableCell align="right">
                      {stop.on_site_minutes != null ? t('reports.siteTime.minutes', { count: stop.on_site_minutes }) : '—'}
                    </TableCell>
                    <TableCell align="right">{renderOverUnder(stop.over_under_minutes)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}
    </Box>
  );
};

export default SiteTimeReport;
